import { create, type StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Session } from '../schemas/session.schema';
import {
  clearStoredSession,
  getJwtOrganizations,
  getJwtRole,
  getJwtSub,
  isSessionCurrent,
  readStoredSession,
  writeStoredSession,
} from '../lib/session';
import { logout as apiLogout, refreshToken as apiRefreshToken } from '../services/auth.service';
import { storeRedirectTarget } from '../lib/redirect';
import { op } from '@/shared/lib/monitoring';

const LOCAL_STORAGE_KEY = 'els.auth.session';

interface AuthState {
  session: Session | null;
  isAuthenticated: () => boolean;
  hasRole: (role: string) => boolean;
  isManagerOrAdmin: () => boolean;
  getUserOrganizations: () => string[];
  setSession: (session: Session) => void;
  clearSession: () => void;
}

let refreshTimer: number | undefined;

const scheduleTokenRefresh = (session: Session) => {
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(
    () => {
      void (async () => {
        const current = useAuthStore.getState().session;
        if (current?.refresh_token !== session.refresh_token) {
          return;
        }
        try {
          const next = await apiRefreshToken(current.refresh_token);
          useAuthStore.getState().setSession(next);
        } catch {
          storeRedirectTarget(window.location.pathname + window.location.search);
          useAuthStore.getState().clearSession();
          window.location.assign('/login');
        }
      })();
    },
    Math.max((session.expires_in - 60) * 1_000, 30_000),
  );
};

const createAuthSlice: StateCreator<AuthState, [['zustand/immer', never]], [], AuthState> = (
  set,
) => ({
  session: readStoredSession(),
  isAuthenticated: () => isSessionCurrent(useAuthStore.getState().session),
  hasRole: (role) => {
    const session = useAuthStore.getState().session;
    return session ? getJwtRole(session.access_token) === role : false;
  },
  isManagerOrAdmin: () => {
    const session = useAuthStore.getState().session;
    if (!session) {
      return false;
    }
    const role = getJwtRole(session.access_token);
    return role === 'admin' || role === 'manager';
  },
  getUserOrganizations: () => {
    const session = useAuthStore.getState().session;
    return session ? getJwtOrganizations(session.access_token) : [];
  },
  setSession: (session) => {
    writeStoredSession(session);
    const sub = getJwtSub(session.access_token);
    if (sub) {
      op.identify({ profileId: sub });
    }
    set((state) => {
      state.session = session;
    });
    scheduleTokenRefresh(session);
  },
  clearSession: () => {
    window.clearTimeout(refreshTimer);
    const session = useAuthStore.getState().session;
    if (session) {
      void apiLogout(session.refresh_token);
    }

    clearStoredSession();
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    op.clear();
    set((state) => {
      state.session = null;
    });
  },
});

export const useAuthStore = create<AuthState>()(
  devtools(immer(createAuthSlice), {
    name: 'auth-store',
  }),
);

const restoredSession = useAuthStore.getState().session;
if (restoredSession) {
  const sub = getJwtSub(restoredSession.access_token);
  if (sub) {
    op.identify({ profileId: sub });
  }
  scheduleTokenRefresh(restoredSession);
}
